import { baseApi, rtkq, RtkqRequest, Http, Status, asky, ListSegment } from '@app/base';
import {
  CareHomeDashboardPatientSummary,
  CreatePatientRequest,
  InvitePatientRequest,
  Patient,
  PatientListItem,
  PatientLookup,
  UpdatePatientEmailRequest,
  UpdatePatientRequest,
  UpdatePatientStatusRequest,
} from './patientsTypes';
import { FilterRule, PagingRule, SortRule } from '@webinex/asky';
import {
  GetPatientDocument,
  GetPatientLookupDocument,
  GetPatientIdsByCareHomeListSegmentDocument,
  GetCareHomeDashboardPatientSummariesDocument,
  GetPatientLookupsListSegmentDocument,
  GetPatientsListSegmentDocument,
} from './graphql';

export const patientsApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getPatientsListSegment: build.query<
      ListSegment<PatientListItem>,
      RtkqRequest<{
        searchValue?: string | null;
        filterRule?: FilterRule | null;
        sortRule?: SortRule | SortRule[] | null;
        pagingRule?: PagingRule | null;
      }>
    >({
      queryFn: rtkq.graphql(GetPatientsListSegmentDocument, {
        select: (x) => x.patientsListSegment,
        vars: ({ searchValue, ...query }) => {
          if (
            !query ||
            query.sortRule == null ||
            (Array.isArray(query.sortRule) && query.sortRule.length === 0)
          )
            query = {
              ...(query ?? {}),
              sortRule: [
                { fieldId: 'firstName', dir: 'asc' },
                { fieldId: 'lastName', dir: 'asc' },
              ],
            };

          return {
            query: query ?? null,
            searchValue: searchValue ?? null,
          };
        },
      }),
      providesTags: (result = { items: [], total: -1 }) => [
        { type: 'main:patient', id: 'list' },
        ...result.items.map(({ id }) => ({ type: 'main:patient' as const, id })),
      ],
    }),

    getPatientLookups: build.query<
      PatientLookup[],
      RtkqRequest<{ statuses?: Status[]; careHomeId?: string }>
    >({
      queryFn: rtkq.graphql(GetPatientLookupsListSegmentDocument, {
        select: (x) => x.patientsListSegment.items,
        vars: ({ statuses, careHomeId }) => ({
          query: {
            filterRule: asky.filter.create([
              !!statuses && {
                fieldId: 'status',
                operator: 'in',
                values: statuses,
              },
              !!careHomeId && {
                fieldId: 'careHomeId',
                operator: '=',
                value: careHomeId,
              },
            ]),
          },
        }),
      }),
      providesTags: (result = []) => [
        { type: 'main:patient', id: 'list' },
        ...result.map(({ id }) => ({ type: 'main:patient' as const, id })),
      ],
    }),

    getPatientIdsByCareHome: build.query<string[], RtkqRequest<{ careHomeId: string; statuses?: Status[] }>>({
      queryFn: rtkq.graphql(GetPatientIdsByCareHomeListSegmentDocument, {
        select: (x) => x.patientsListSegment.items.map((x: { id: string }) => x.id),
        vars: ({ careHomeId, statuses }) => ({
          query: {
            filterRule: asky.filter.create([
              {
                fieldId: 'careHomeId',
                operator: '=',
                value: careHomeId,
              },
              !!statuses && {
                fieldId: 'status',
                operator: 'in',
                values: statuses,
              },
            ]),
          },
        }),
      }),
      providesTags: (result = []) => [
        { type: 'main:patient', id: 'list' },
        ...result.map((id) => ({ type: 'main:patient' as const, id })),
      ],
    }),

    getCareHomeDashboardPatientSummaries: build.query<
      CareHomeDashboardPatientSummary[],
      RtkqRequest<{ careHomeId: string }>
    >({
      queryFn: rtkq.graphql(GetCareHomeDashboardPatientSummariesDocument, {
        select: (x) => x.patientsListSegment.items as CareHomeDashboardPatientSummary[],
        vars: ({ careHomeId }) => ({
          query: {
            filterRule: asky.filter.create([
              {
                fieldId: 'careHomeId',
                operator: '=',
                value: careHomeId,
              },
              {
                fieldId: 'status',
                operator: '=',
                value: Status.ACTIVE,
              },
            ]),
          },
        }),
      }),
      providesTags: (result = []) => [
        { type: 'main:patient', id: 'list' },
        ...result.map(({ id }) => ({ type: 'main:patient' as const, id })),
        'medical-record:encounter',
        'schedule:appointment-event',
      ],
    }),

    getPatient: build.query<Patient, RtkqRequest<{ id: string }>>({
      queryFn: rtkq.graphql(GetPatientDocument, {
        select: (x) => x.patient as Patient,
        vars: ({ id }) => ({ id }),
      }),
      providesTags: (_, __, { id }) => [{ type: 'main:patient', id }],
    }),

    getPatientLookup: build.query<PatientLookup, RtkqRequest<{ id: string }>>({
      queryFn: rtkq.graphql(GetPatientLookupDocument, {
        select: (x) => x.patient as PatientLookup,
        vars: ({ id }) => ({ id }),
      }),
      providesTags: (_, __, { id }) => [{ type: 'main:patient', id }],
    }),

    createPatient: build.mutation<string, RtkqRequest<CreatePatientRequest>>({
      queryFn: rtkq.axios((args) => Http.post(`/api/patient/create`, args)),
      invalidatesTags: [{ type: 'main:patient', id: 'list' }],
    }),

    updatePatient: build.mutation<void, RtkqRequest<UpdatePatientRequest>>({
      queryFn: rtkq.axios((args) => Http.put(`/api/patient/${args.id}`, args)),
      invalidatesTags: (_, __, { id }) => [{ type: 'main:patient', id }],
    }),

    invitePatient: build.mutation<void, RtkqRequest<InvitePatientRequest>>({
      queryFn: rtkq.axios((args) => Http.post(`/api/patient/invite`, args)),
      invalidatesTags: (_, __, { id }) => [
        { type: 'main:patient', id: 'list' },
        { type: 'main:patient', id },
      ],
    }),

    updatePatientEmail: build.mutation<void, RtkqRequest<UpdatePatientEmailRequest>>({
      queryFn: rtkq.axios((args) => Http.put(`/api/patient/${args.id}/email`, args)),
      invalidatesTags: (_, __, { id }) => [
        { type: 'main:patient', id: 'list' },
        { type: 'main:patient', id },
      ],
    }),

    updatePatientStatus: build.mutation<void, RtkqRequest<UpdatePatientStatusRequest>>({
      queryFn: rtkq.axios((args) => Http.put(`/api/patient/${args.id}/status`, args)),
      invalidatesTags: (_, __, { id }) => [
        { type: 'main:patient', id },
        { type: 'main:patient', id: 'list' },
      ],
    }),
  }),
});

export const {
  useGetPatientsListSegmentQuery,
  useGetPatientQuery,
  useGetPatientLookupQuery,
  useCreatePatientMutation,
  useUpdatePatientMutation,
  useLazyGetPatientLookupsQuery,
  useGetPatientIdsByCareHomeQuery,
  useGetCareHomeDashboardPatientSummariesQuery,
  useInvitePatientMutation,
  useUpdatePatientEmailMutation,
  useUpdatePatientStatusMutation,
  useLazyGetPatientQuery,
} = patientsApi;
